/**
 * AdminCorneliusMemories
 *
 * Inner content for /admin/cornelius-memories.  Wrapped by
 * AdminCorneliusMemoriesPage (in App.js) which provides Sidebar + top-nav +
 * content-wrapper chrome.
 *
 * Styling: uses App.css CSS variables (--card-bg, --primary-color, …) so the
 * page automatically tracks the OPO theme (light/dark) and visual language.
 */

import React, { useState, useEffect, useCallback } from 'react';

const SENSITIVITY_COLOR = {
  public:    'var(--text-secondary)',
  private:   'var(--primary-color)',
  sensitive: 'var(--danger-color)',
};

const cardStyle = (selected) => ({
  background:   selected ? 'rgba(60,252,249,0.06)' : 'var(--card-bg)',
  border:       `1px solid ${selected ? 'var(--primary-color)' : 'var(--border-color)'}`,
  borderRadius: 'var(--border-radius)',
  padding:      '12px 14px',
  marginBottom: 8,
  color:        'var(--text-primary)',
  boxShadow:    'var(--shadow-sm)',
});

const inputStyle = {
  padding: '7px 12px',
  borderRadius: 'var(--border-radius)',
  border: '1px solid var(--border-color)',
  background: 'var(--surface-color)',
  color: 'var(--text-primary)',
  fontSize: 13,
  outline: 'none',
};

function btn(variant) {
  const map = {
    primary: { background: 'var(--primary-color)',  color: 'var(--text-on-primary)' },
    success: { background: 'var(--success-color)',  color: '#fff' },
    warn:    { background: 'var(--warning-color)',  color: '#1e293b' },
    danger:  { background: 'var(--danger-color)',   color: '#fff' },
    ghost:   { background: 'transparent',           color: 'var(--text-secondary)', border: '1px solid var(--border-color)' },
  };
  return {
    padding: '5px 10px', borderRadius: 'var(--border-radius)', border: 'none',
    fontSize: 12, cursor: 'pointer', fontWeight: 600,
    ...map[variant],
  };
}

function fmtConfidence(c) {
  if (c == null) return '—';
  const n = parseFloat(c);
  if (Number.isNaN(n)) return '—';
  return `${Math.round(n * 100)}%`;
}

function confidenceColor(c) {
  if (c == null) return 'var(--text-secondary)';
  const n = parseFloat(c);
  if (n >= 0.85) return 'var(--success-color)';
  if (n >= 0.65) return 'var(--warning-color)';
  return 'var(--danger-color)';
}

function statusOptions() {
  return [
    { id: 'live',        label: 'Live (used by Cornelius)' },
    { id: 'quarantined', label: 'Quarantined (web-fetched, awaiting review)' },
    { id: 'superseded',  label: 'Superseded (older versions)' },
    { id: 'forgotten',   label: 'Forgotten (user-flagged)' },
    { id: 'all',         label: 'All' },
  ];
}

function pill(color, bg) {
  return {
    padding: '2px 7px',
    borderRadius: 10,
    background: bg || 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    color: color || 'var(--text-primary)',
    fontWeight: 600,
    fontSize: 11,
  };
}

function MemoryRow({ memory, onAction, onSelect, selected }) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(memory.content || '');
  const [importance, setImportance] = useState(memory.importance ?? 5);

  return (
    <div style={cardStyle(selected)}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onSelect(memory.id)}
          style={{ marginTop: 4 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Top metadata strip */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-secondary)' }}>
            <span style={pill('var(--text-primary)')}>{memory.memory_type}</span>
            <span style={pill(SENSITIVITY_COLOR[memory.sensitivity] || 'var(--text-secondary)')}>
              {memory.sensitivity || 'private'}
            </span>
            <span title="Importance (1–10)">imp: <strong style={{ color: 'var(--text-primary)' }}>{memory.importance}</strong></span>
            <span title="Confidence (0–1)" style={{ color: confidenceColor(memory.confidence) }}>
              conf: <strong>{fmtConfidence(memory.confidence)}</strong>
            </span>
            {memory.quarantined    && <span style={{ color: 'var(--warning-color)', fontWeight: 600 }}>QUARANTINED</span>}
            {memory.superseded_at  && <span style={{ color: 'var(--text-secondary)' }}>superseded</span>}
            {memory.user_forgotten && <span style={{ color: 'var(--danger-color)' }}>forgotten</span>}
            <span title="Updated at">{memory.updated_at ? new Date(memory.updated_at).toLocaleString() : ''}</span>
          </div>

          {/* Content */}
          {editing ? (
            <div style={{ marginTop: 8 }}>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'vertical',
                  background: 'var(--surface-color)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)',
                  padding: 8, fontSize: 13, fontFamily: 'inherit',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  importance
                  <input
                    type="number" min={1} max={10} value={importance}
                    onChange={e => setImportance(parseInt(e.target.value, 10) || 5)}
                    style={{
                      marginLeft: 6, width: 60,
                      background: 'var(--surface-color)', color: 'var(--text-primary)',
                      border: '1px solid var(--border-color)', borderRadius: 4,
                      padding: '2px 4px',
                    }}
                  />
                </label>
                <button
                  onClick={async () => { await onAction('edit', memory.id, { content, importance }); setEditing(false); }}
                  style={btn('primary')}
                >Save edit</button>
                <button
                  onClick={async () => { await onAction('supersede', memory.id, { content, importance, memory_type: memory.memory_type }); setEditing(false); }}
                  style={btn('warn')}
                  title="Insert as new version; keep old row for audit"
                >Save as new version (supersede)</button>
                <button onClick={() => { setEditing(false); setContent(memory.content); }} style={btn('ghost')}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{
              marginTop: 6, fontSize: 13, color: 'var(--text-primary)',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>{memory.content}</div>
          )}

          {/* Provenance */}
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-secondary)', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {memory.source_tool && (
              <span>via <code style={{ color: 'var(--primary-color)' }}>{memory.source_tool}</code></span>
            )}
            {memory.source_task_id && (
              <span>task <code>{String(memory.source_task_id).slice(0, 8)}</code></span>
            )}
            {memory.source_url && (
              <a href={memory.source_url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary-color)' }}>
                {memory.source_url.length > 60 ? memory.source_url.slice(0, 60) + '…' : memory.source_url}
              </a>
            )}
            <span>tier: {memory.memory_tier || '—'}</span>
            <span>id: {memory.id}</span>
          </div>

          {/* Actions */}
          {!editing && (
            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button onClick={() => setEditing(true)} style={btn('primary')}>Edit</button>
              {memory.quarantined && (
                <button onClick={() => onAction('approve', memory.id)} style={btn('success')}>
                  Approve (clear quarantine)
                </button>
              )}
              {!memory.user_forgotten && (
                <button
                  onClick={() => window.confirm('Cornelius will never use this memory again. Continue?') && onAction('forget', memory.id)}
                  style={btn('warn')}
                  title="Soft flag user_forgotten=true. Row stays in DB for audit."
                >Never remember this</button>
              )}
              <button
                onClick={() => window.confirm('Hard delete this row? This cannot be undone.') && onAction('delete', memory.id)}
                style={btn('danger')}
              >Delete (right-to-deletion)</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SourcesPanel({ sources }) {
  if (!sources || sources.length === 0) return null;
  const total = sources.reduce((s, x) => s + (x.total || 0), 0);
  return (
    <div style={{
      background: 'var(--card-bg)',
      border: '1px solid var(--border-color)',
      borderRadius: 'var(--border-radius)',
      padding: '12px 14px', marginBottom: 16,
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
        Where this user's live memories come from ({total} rows)
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {sources.map(s => {
          const pct = total ? Math.round((s.total / total) * 100) : 0;
          return (
            <div key={s.source_tool} style={{
              background: 'var(--surface-color)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--border-radius)',
              padding: '6px 10px', fontSize: 12, color: 'var(--text-primary)',
            }}>
              <code style={{ color: 'var(--primary-color)' }}>{s.source_tool}</code>
              <span style={{ marginLeft: 6 }}>{s.total} ({pct}%)</span>
              <span style={{ marginLeft: 6, color: confidenceColor(s.avg_confidence) }}>
                avg {fmtConfidence(s.avg_confidence)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminCorneliusMemories() {
  const [memories, setMemories] = useState([]);
  const [sources,  setSources]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [userId,   setUserId]   = useState('13');
  const [status,   setStatus]   = useState('live');
  const [search,   setSearch]   = useState('');
  const [selected, setSelected] = useState(new Set());
  const [error,    setError]    = useState('');
  const [msg,      setMsg]      = useState('');

  // Initial user_id from query string
  useEffect(() => {
    const u = new URLSearchParams(window.location.search).get('user_id');
    if (u && /^\d+$/.test(u)) setUserId(u);
  }, []);

  const fetchAll = useCallback(async () => {
    if (!userId.trim()) { setMemories([]); setSources([]); return; }
    setLoading(true); setError('');
    try {
      const [memRes, srcRes] = await Promise.all([
        fetch(`/api/opo/cornelius-memories?user_id=${userId.trim()}&status=${status}&limit=200`),
        fetch(`/api/opo/cornelius-memories/sources?user_id=${userId.trim()}`),
      ]);
      if (!memRes.ok) throw new Error(`memories HTTP ${memRes.status}`);
      const memData = await memRes.json();
      setMemories(memData.memories || []);
      if (srcRes.ok) {
        const srcData = await srcRes.json();
        setSources(srcData.sources || []);
      }
      setSelected(new Set());
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [userId, status]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = memories.filter(m => {
    if (!search.trim()) return true;
    return (m.content || '').toLowerCase().includes(search.toLowerCase())
        || (m.source_tool || '').toLowerCase().includes(search.toLowerCase());
  });

  async function doAction(action, memoryId, body) {
    setMsg('');
    try {
      const opts = {
        method: action === 'delete' ? 'DELETE' : (action === 'edit' ? 'PUT' : 'POST'),
        headers: { 'Content-Type': 'application/json' },
      };
      let url;
      if (action === 'edit' || action === 'delete') {
        url = `/api/opo/cornelius-memories/${memoryId}?user_id=${userId}`;
      } else {
        url = `/api/opo/cornelius-memories/${memoryId}/${action}?user_id=${userId}`;
      }
      if (body) opts.body = JSON.stringify({ ...body, user_id: userId });
      const r = await fetch(url, opts);
      if (!r.ok) throw new Error(`${action} HTTP ${r.status}`);
      setMsg(`${action} ok (memory ${memoryId})`);
      await fetchAll();
    } catch (e) {
      setError(e.message);
    }
  }

  async function bulk(action) {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`${action} ${ids.length} selected memories?`)) return;
    for (const id of ids) await doAction(action, id);
    setSelected(new Set());
  }

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
        Review, edit, supersede, forget, or hard-delete what Cornelius remembers about a user.
        Implements Tier-1 of the persistent-memory roadmap.
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          value={userId}
          onChange={e => setUserId(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchAll()}
          placeholder="User ID"
          style={{ ...inputStyle, width: 120 }}
        />
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          {statusOptions().map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {['live', 'quarantined', 'superseded', 'forgotten', 'all'].map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              style={{
                ...btn('ghost'),
                fontSize: 11,
                padding: '4px 10px',
                ...(status === s ? { borderColor: 'var(--primary-color)', color: 'var(--primary-color)', fontWeight: 700 } : {}),
              }}
            >
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter content or source_tool…"
          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
        />
        <button onClick={fetchAll} style={{ ...btn('primary'), padding: '7px 16px', fontSize: 13 }}>Refresh</button>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div style={{
          background: 'var(--card-bg)', border: '1px solid var(--primary-color)',
          borderRadius: 'var(--border-radius)', padding: '8px 12px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>{selected.size} selected</span>
          <button onClick={() => bulk('approve')} style={btn('success')}>Approve all</button>
          <button onClick={() => bulk('forget')}  style={btn('warn')}>Forget all</button>
          <button onClick={() => bulk('delete')}  style={btn('danger')}>Delete all</button>
          <button onClick={() => setSelected(new Set())} style={btn('ghost')}>Clear selection</button>
        </div>
      )}

      <SourcesPanel sources={sources} />

      {error && <div style={{ color: 'var(--danger-color)', fontSize: 13, marginBottom: 12 }}>Error: {error}</div>}
      {msg   && <div style={{ color: 'var(--success-color)', fontSize: 13, marginBottom: 12 }}>{msg}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          {userId ? `No "${status}" memories for user ${userId}.` : 'Enter a user ID above.'}
        </div>
      )}

      {filtered.map(m => (
        <MemoryRow
          key={m.id}
          memory={m}
          selected={selected.has(m.id)}
          onSelect={(id) => setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          })}
          onAction={doAction}
        />
      ))}
    </div>
  );
}
